import React, { useEffect, useRef } from 'react';

/**
 * Subtle radial spotlight that follows the cursor with the brand-lime tint.
 * Uses mix-blend-mode: screen so it lightens whatever's behind it instead of
 * tinting the foreground content. Hidden on touch devices (no real cursor).
 */
const MouseGlow = () => {
  const ref = useRef(null);
  const target = useRef({ x: 0, y: 0 });
  const current = useRef({ x: 0, y: 0 });
  const raf = useRef(null);

  useEffect(() => {
    // No useful glow on touch devices
    if (window.matchMedia('(pointer: coarse)').matches) return;

    const el = ref.current;
    if (!el) return;

    // Center as the starting position so the glow isn't stuck at 0,0
    target.current.x = window.innerWidth / 2;
    target.current.y = window.innerHeight / 2;
    current.current = { ...target.current };

    const onMove = (e) => {
      target.current.x = e.clientX;
      target.current.y = e.clientY;
    };

    const tick = () => {
      // Eased follow — gives a subtle lag instead of snapping
      current.current.x += (target.current.x - current.current.x) * 0.12;
      current.current.y += (target.current.y - current.current.y) * 0.12;
      el.style.setProperty('--mx', `${current.current.x}px`);
      el.style.setProperty('--my', `${current.current.y}px`);
      raf.current = requestAnimationFrame(tick);
    };

    window.addEventListener('mousemove', onMove, { passive: true });
    raf.current = requestAnimationFrame(tick);

    return () => {
      window.removeEventListener('mousemove', onMove);
      if (raf.current) cancelAnimationFrame(raf.current);
    };
  }, []);

  return (
    <div
      ref={ref}
      aria-hidden="true"
      data-testid="mouse-glow"
      className="pointer-events-none fixed inset-0 z-30 hidden md:block"
      style={{
        mixBlendMode: 'screen',
        background:
          'radial-gradient(600px circle at var(--mx, 50%) var(--my, 50%), rgba(132, 204, 22, 0.16), rgba(132, 204, 22, 0) 65%)',
      }}
    />
  );
};

export default MouseGlow;
